// VISUAL_OVERHAUL §"Featured cards row" — replaces the previous 3-up share
// cards row at the top of the report. Asymmetric 60/40 grid on desktop:
//   Left column (60%): All-Time Wins — every owner, no truncation
//   Right column (40%): vertical stack of [Titles, Top Blowouts]
// On mobile, all three stack to single column.
//
// These cards are data-derived from stats (same source as the existing PNG
// share cards), but rendered as native HTML for the on-page hero treatment.
// The PNG share cards still exist at the BOTTOM of the page for sharing.

import { useMemo } from "react";
import theme from "../../data/theme";

const DISPLAY = theme.fonts.almanac;

export default function FeaturedCardsRow({ stats }) {
  // A1.5 §2.2 — equal-width 3-card row. The previous All-Time Wins card
  // was dropped (its W/L data is a strict subset of the Career Totals
  // featured table that renders above this row).
  const titleRows = useMemo(() => buildTitleRows(stats), [stats]);
  const blowouts = useMemo(() => buildBlowouts(stats), [stats]);
  const spotlight = useMemo(() => buildSpotlight(stats), [stats]);

  if (!titleRows.length && !blowouts.length && !spotlight.length) return null;

  return (
    <section className="almanac-featured" aria-label="Featured leaderboards">
      <div className="almanac-featured-three">
        {titleRows.length > 0 && <TitlesCard rows={titleRows} />}
        {blowouts.length > 0 && <BlowoutsCard rows={blowouts} />}
        {spotlight.length > 0 && <SpotlightCard rows={spotlight} />}
      </div>
    </section>
  );
}

function TitlesCard({ rows }) {
  return (
    <div className="almanac-card almanac-card--titles">
      <div className="almanac-card-eyebrow">Titles</div>
      <ul className="almanac-titles-list">
        {rows.map((r, i) => (
          <li key={r.owner} className="almanac-titles-row">
            <span className="almanac-titles-medal">{medalFor(i)}</span>
            <span className="almanac-titles-owner">{r.owner}</span>
            <span className="almanac-titles-count" style={{ fontFamily: DISPLAY }}>{r.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BlowoutsCard({ rows }) {
  return (
    <div className="almanac-card almanac-card--blowouts">
      <div className="almanac-card-eyebrow">Top Blowouts</div>
      <ol className="almanac-blowouts-list">
        {rows.map((b, i) => (
          <li key={`${b.year}-${b.week}-${b.owner}`} className="almanac-blowouts-row">
            <div className="almanac-blowouts-headline">
              <span className="almanac-blowouts-rank">{i + 1}.</span>
              <span className="almanac-blowouts-winner">{b.owner}</span>
              <span className="almanac-blowouts-score" style={{ fontFamily: DISPLAY }}>{fmt1(b.pts)}</span>
              <span className="almanac-blowouts-sep">over</span>
              <span className="almanac-blowouts-loser">{b.opp}</span>
              <span className="almanac-blowouts-score" style={{ fontFamily: DISPLAY }}>{fmt1(b.oppPts)}</span>
            </div>
            <div className="almanac-blowouts-meta">
              +{fmt1(b.margin)} margin · {b.year} Wk {b.week}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// A1.6 R2 #10 — Titles card metric IS championships (every row is a
// "gold"); positional medals were misleading. Use a single trophy for
// every row instead.
function medalFor() {
  return "🏆";
}

function fmt1(n) { return n == null ? "—" : Number(n).toFixed(1); }

// Every owner with at least one title, sorted by count desc. The medal
// emoji is positional (gold/silver/bronze for top 3) regardless of ties —
// canonical "1st place is gold" semantics.
function buildTitleRows(stats) {
  const counts = stats?.championships?.titleCounts || {};
  const rows = Object.entries(counts)
    .map(([owner, years]) => ({ owner, count: Array.isArray(years) ? years.length : 0 }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
  return rows;
}

// Top 3 (not top 1 like the old single-card share). singleGameRow shape
// (year, week, owner, pts, opp, oppPts, margin) per funStats.singleGameRow.
function buildBlowouts(stats) {
  const list = stats?.singleGame?.biggestBlowouts || [];
  return list.slice(0, 3);
}

// A1 Patch round — Awards Spotlight card pulls 4 fixed awards from the
// new stats.almanacAwards aggregator. Universal slate — same 4 picks for
// both redraft and dynasty leagues. Dynasty's extra 2 YoY awards only
// appear in the Awards section's expanded view, not the spotlight.
const SPOTLIGHT_SLUGS = ["bridesmaid", "heartbreaker", "escapeArtist", "titleDrought"];

function buildSpotlight(stats) {
  const awards = stats?.almanacAwards || {};
  const out = [];
  for (const slug of SPOTLIGHT_SLUGS) {
    const a = awards[slug];
    if (a) {
      out.push({
        label: a.label,
        icon: a.icon,
        owner: a.owner,
        context: a.context,
      });
    }
  }
  return out;
}

function SpotlightCard({ rows }) {
  return (
    <div className="almanac-card almanac-card--spotlight">
      <div className="almanac-card-eyebrow">Awards Spotlight</div>
      <ul className="almanac-spotlight-list">
        {rows.map((r, i) => (
          <li key={i} className="almanac-spotlight-row">
            <span className="almanac-spotlight-icon">{r.icon}</span>
            <div className="almanac-spotlight-text">
              <div className="almanac-spotlight-label">{r.label}</div>
              <div className="almanac-spotlight-owner">
                <span className="almanac-spotlight-owner-name">{r.owner}</span>
                <span className="almanac-spotlight-context"> · {r.context}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
